// BodBrief — board-friendly executive summary (DATA-72).
//
// The monthly ml-board-report cron generates a narrative report and emails
// it to LEADERSHIP_DIST_LIST. This page is the LIVE complement: at any
// moment, board members can pull up the latest report, browse the archive,
// and see live key metrics + active alerts alongside.
//
// Plain language. Clean spacing. Optimized for "open before the 7:30 PM
// Monday meeting and skim in 90 seconds."

import { useEffect, useState, type ReactElement } from "react";
import { useRealtimeCursor } from "../hooks/useRealtimeCursor";
import AskBoardPanel from "../components/ai/AskBoardPanel";
import PageHeader from "../components/shared/PageHeader";
import Banner from "../components/shared/Banner";
import type { AggregatesResponse } from "../hooks/useDashboard";

/** Coerce a dash_aggregates value (number-or-numeric-string) to a number|null. */
function num(v: unknown): number | null {
  if (typeof v === "number") return isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "" && isFinite(Number(v)))
    return Number(v);
  return null;
}

interface ReportListItem {
  id: number;
  year_month: string; // "YYYY-MM"
  title: string;
  tldr: string;
  generated_at: string;
  delivery: string | null;
}

interface ReportFull extends ReportListItem {
  body_html: string;
  model_used: string | null;
  prompt_version: string | null;
}

interface AlertEvent {
  rule_id: string;
  subject_id: string | null;
  severity: string;
  emitted_at: string;
}

interface NextMeetingResponse {
  next: { id: number; scheduled_at: string; label: string } | null;
  days_until: number | null;
  recent_snapshots: Array<{
    id: number;
    snapshot_at: string;
    days_to_meeting: number;
    generator: string;
  }>;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

/**
 * Strip HTML tags and re-paragraph the body. The board reports are emitted by
 * the local LLM as `body_html`; we deliberately render text content only (no
 * dangerouslySetInnerHTML), which is XSS-safe by construction. Rich formatting
 * (tables/links) is intentionally flattened to paragraphs/bullets — plain text
 * is the supported rendering for this surface.
 */
function renderReportBody(html: string): ReactElement[] {
  // Convert common block tags to newlines so paragraph segmentation works
  const normalized = html
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/?\s*(p|h1|h2|h3|h4|li|ul|ol|div)\s*[^>]*>/gi, "\n")
    // strip everything else
    .replace(/<[^>]*>/g, "")
    // collapse extra whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const blocks = normalized.split(/\n{2,}/);
  return blocks.map((b, i) => {
    // Detect bullet lists by leading bullet/dash
    const lines = b
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const looksLikeList =
      lines.length > 1 && lines.every((l) => /^[-•*]\s+/.test(l));
    if (looksLikeList) {
      return (
        <ul
          key={i}
          className="list-disc list-inside space-y-1 text-sm text-theme-secondary mb-3"
        >
          {lines.map((l, j) => (
            <li key={j}>{l.replace(/^[-•*]\s+/, "")}</li>
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        className="text-sm text-theme-secondary leading-relaxed mb-3 whitespace-pre-wrap"
      >
        {b}
      </p>
    );
  });
}

export default function BodBrief() {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [selectedYm, setSelectedYm] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportFull | null>(null);
  const [aggregates, setAggregates] = useState<AggregatesResponse | null>(null);
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [nextMeeting, setNextMeeting] = useState<NextMeetingResponse | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);
  // DATA-70 — re-fetch when the BOD-snapshot cursor advances (a new T-3/T-2/T-1/T-0
  // snapshot landed) without paying for a full reload.
  const { updatedAt: snapshotsCursor } = useRealtimeCursor("bod_snapshots");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchJson<{ reports: ReportListItem[] }>("/api/ai/board-reports"),
      fetchJson<AggregatesResponse>("/api/dashboard/aggregates").catch(
        () => null,
      ),
      fetchJson<{ events: AlertEvent[] }>("/api/admin/ai/alerts").catch(() => ({
        events: [],
      })),
      fetchJson<NextMeetingResponse>("/api/bod/next-meeting").catch(() => null),
    ])
      .then(([rpts, aggs, al, nm]) => {
        if (cancelled) return;
        setReports(rpts.reports ?? []);
        if (rpts.reports?.[0]) setSelectedYm(rpts.reports[0].year_month);
        if (aggs) setAggregates(aggs);
        setAlerts((al?.events ?? []).slice(0, 5));
        if (nm) setNextMeeting(nm);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotsCursor]);

  useEffect(() => {
    if (!selectedYm) {
      setSelectedReport(null);
      return;
    }
    let cancelled = false;
    fetchJson<ReportFull>(`/api/ai/board-reports/${selectedYm}`)
      .then((r) => {
        if (!cancelled) setSelectedReport(r);
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedYm]);

  // Derive the four headline metrics from the nested dash_aggregates payload
  // using the canonical key names the nightly sync actually writes. (The page
  // previously read flat top-level fields that never exist on the response, so
  // every card rendered "—".)
  const agg = aggregates?.aggregates ?? {};
  const activeMembers = num(agg["active_member_count"]);
  const inPipeline = num(agg["pipeline_active_current"]);
  const retentionRate = num(agg["first_year_retention_rate"]);
  // Stored as a 0..1 rate; some historical rows are already a percent (>1).
  const retentionPct =
    retentionRate == null
      ? null
      : retentionRate > 1
        ? retentionRate
        : retentionRate * 100;
  const ytdJoined = num(agg["ytd_joined"]);
  const ytdSeparated = num(agg["ytd_separated"]);
  const netChangeYtd =
    ytdJoined == null || ytdSeparated == null ? null : ytdJoined - ytdSeparated;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <PageHeader
          title="BOD Brief"
          subtitle="Monthly board report archive plus live key metrics. Designed to be skimmed before the 7:30 PM meeting."
        />
      </div>

      {err && (
        <Banner variant="error" className="mb-4">
          {err}
        </Banner>
      )}

      {nextMeeting?.next && <NextMeetingBanner data={nextMeeting} />}

      {/* Board-mode Ask Ember — DATA-73b. Inline (no FAB), governance prompt,
          BOD-corpus only. The floating staff-mode AskPanel is hidden on this
          page so the inline panel is the canonical surface. */}
      <div className="mb-6">
        <AskBoardPanel />
      </div>

      {/* Live signals strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Active members" value={activeMembers} />
        <MetricCard label="In pipeline" value={inPipeline} />
        <MetricCard
          label="1st-year retention"
          value={retentionPct}
          formatter={(n) => `${n.toFixed(1)}%`}
          /* No target: "50% by Q1 2027" removed 2026-08-26 per Riley's ruling
             of 2026-08-25 15:22Z. No approved document contains it — the 2026 R&R
             OKRs explicitly DECLINE to set a numeric retention target, and Riley
             withdrew the 50% line herself on 07-28 because it re-installed a
             retired national comparator. This is a BOARD-facing card, so an
             unsourced target here reads as a departmental commitment.
             Do not restore without an approved source and the R&R Director's word. */
        />
        <MetricCard
          label="Net change YTD"
          value={netChangeYtd}
          formatter={(n) => (n > 0 ? `+${n}` : `${n}`)}
          /* No target: "+10 by Q1 2027" was removed 2026-08-26. Riley (R&R lane
             owner) ruled 2026-07-29 that it exists in no approved document. This
             card is on a BOARD-facing brief, so an unsourced target here reads as
             a commitment the department has not made. MetricCard renders cleanly
             without one. Do not restore without an approved source. */
        />
      </section>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar: report archive */}
        <aside>
          <p className="text-xs font-medium uppercase tracking-wider text-theme-muted mb-2">
            Reports
          </p>
          <ul className="space-y-1">
            {reports.length === 0 && !err && (
              <li className="text-sm text-theme-muted">No reports yet.</li>
            )}
            {reports.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setSelectedYm(r.year_month)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                    selectedYm === r.year_month
                      ? "bg-theme-overlay border-theme-default text-theme-primary"
                      : "border-theme-subtle text-theme-secondary hover:bg-theme-overlay/40"
                  }`}
                >
                  <div className="font-mono text-xs">{r.year_month}</div>
                  <div className="text-sm truncate">{r.title}</div>
                </button>
              </li>
            ))}
          </ul>

          {/* Active alerts mini-list */}
          {alerts.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-medium uppercase tracking-wider text-theme-muted mb-2">
                Active alerts (24h)
              </p>
              <ul className="space-y-1.5">
                {alerts.map((a, i) => (
                  <li
                    key={i}
                    className="text-xs px-2 py-1.5 rounded border border-theme-subtle bg-theme-overlay/40"
                  >
                    <div className="font-medium text-theme-primary">
                      {a.rule_id}
                    </div>
                    {a.subject_id && (
                      <div className="font-mono text-[10px] text-theme-muted">
                        {a.subject_id}
                      </div>
                    )}
                    <div className="text-[10px] text-theme-muted mt-0.5 uppercase">
                      {a.severity}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Main: selected report body */}
        <main>
          {!selectedReport && !err && reports.length > 0 && (
            <p className="text-sm text-theme-muted">Loading report…</p>
          )}
          {selectedReport && (
            <article>
              <div className="mb-4 pb-4 border-b border-theme-subtle">
                <h2 className="text-xl font-semibold text-theme-primary">
                  {selectedReport.title}
                </h2>
                <p className="text-xs text-theme-muted mt-1 font-mono">
                  generated{" "}
                  {new Date(selectedReport.generated_at).toLocaleString()}
                  {selectedReport.model_used &&
                    ` · ${selectedReport.model_used}`}
                </p>
                {selectedReport.tldr && (
                  <p className="mt-3 text-sm font-medium text-theme-secondary border-l-2 border-brand-500 pl-3">
                    {selectedReport.tldr}
                  </p>
                )}
              </div>
              <div className="report-body">
                {renderReportBody(selectedReport.body_html ?? "")}
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}

function NextMeetingBanner({ data }: { data: NextMeetingResponse }) {
  if (!data.next) return null;
  const when = new Date(data.next.scheduled_at);
  const days = data.days_until ?? 0;
  const dayLabel =
    days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
  const snaps = data.recent_snapshots ?? [];
  const buckets: Record<number, boolean> = {};
  for (const s of snaps) buckets[s.days_to_meeting] = true;
  return (
    <section className="mb-4 rounded-lg border border-theme-default bg-theme-overlay/60 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wider text-theme-muted">
            Next BOD meeting
          </p>
          <p className="text-base font-semibold text-theme-primary">
            {when.toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}{" "}
            <span className="text-theme-muted font-normal">·</span>{" "}
            <span className="text-theme-secondary">{dayLabel}</span>
          </p>
          <p className="text-xs text-theme-muted font-mono mt-0.5">
            {data.next.label}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
          {[3, 2, 1, 0].map((d) => (
            <span
              key={d}
              className={`px-1.5 py-0.5 rounded border ${
                buckets[d]
                  ? "border-brand-500/60 bg-brand-500/10 text-brand-300"
                  : "border-theme-subtle text-theme-muted"
              }`}
              title={
                buckets[d]
                  ? `Snapshotted at T-${d}`
                  : `T-${d} not yet snapshotted`
              }
            >
              T-{d}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function MetricCard({
  label,
  value,
  formatter,
  target,
}: {
  label: string;
  value: number | null;
  formatter?: (n: number) => string;
  target?: string;
}) {
  const display =
    value === null ? "—" : formatter ? formatter(value) : String(value);
  return (
    <div className="rounded-lg border border-theme-subtle p-3 bg-theme-overlay/40">
      <p className="text-xs uppercase tracking-wider text-theme-muted">
        {label}
      </p>
      <p className="text-2xl font-semibold text-theme-primary mt-1 font-mono">
        {display}
      </p>
      {target && (
        <p className="text-xs text-theme-muted mt-0.5">target: {target}</p>
      )}
    </div>
  );
}
